import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from './MyPrivateTutor.module.scss';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import { getConversationDurationFragments } from '@/components/TutorReportModal/TutorTopicReport';
import { EllReport } from '@mavatar/melingo-voice-and-avatar-sdk';
import {
  GetStatisticsResponse,
  GetGroupReportResponse,
  GetGroupReportTopicsResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import StatisticsBlock, {
  StatisticsBlockProps,
} from '@/components/TutorDashboard/StatisticBlock/StatisticBlock';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { TeacherGroup } from '@/types/user';
import { progressService } from '@/services';
import { ClassProgressResponse } from '@/types/response/progress';
import { ClassOverviewData } from '@/types/progress';
import GenericTable from '@/components/GenericTable/GenericTable';
import { FilterField } from '@/components/GenericTable/BimloaFilter/BimloaFilter';
import PageHeader from '@/components/GenericTable/PageHeader/PageHeader';
import DropDown, { Option } from '@/components/GenericTable/DropDown/DropDown';
import { UserStatusEnum } from '@/types/register';
import Button from '@/components/GenericTable/Button/Button';
import Icon from '@/components/GenericTable/Icon/Icon';
import { formatDateToStandard } from '@/utils/formatters';

const getConversationDurationStr = (durationInSeconds: number) => {
  const fragments = getConversationDurationFragments(durationInSeconds);

  return fragments.hours ? (
    <>
      {fragments.hours}
      <span className='statisticUnits'>h</span> {fragments.minutes}
      <span className='statisticUnits'>m</span> {fragments.seconds}
      <span className='statisticUnits'>s</span>
    </>
  ) : fragments.minutes ? (
    <>
      {fragments.minutes}
      <span className='statisticUnits'>m</span> {fragments.seconds}
      <span className='statisticUnits'>s</span>
    </>
  ) : (
    <>
      {fragments.seconds}
      <span className='statisticUnits'>s</span>
    </>
  );
};

interface GroupViewProps {
  teachersClasses: any;
  selectedGroup: TeacherGroup;
  students: { [student_id: string]: ClassOverviewData };
  setStudents: React.Dispatch<React.SetStateAction<{ [student_id: string]: ClassOverviewData }>>;
  openReport: (report: GetGroupReportTopicsResponse[], userId: string) => void;
}

const GroupView = ({
  teachersClasses,
  selectedGroup,
  students,
  setStudents,
  openReport,
}: GroupViewProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [studentsTopics, setStudentsTopics] = useState<GetGroupReportResponse>({});
  const { user } = useAppSelector(authSelector);
  const [filters, setFilters] = useState<Record<string, Option>>({});
  const [statisticsFilters, setStatisticsFilters] = useState<Option>({
    value: 'week',
    label: t('my_tutor_page.statistics.filter.week'),
  });

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    setStudents({});
    setStudentsTopics({});
    setFilters({});
    setStatisticsFilters({ value: 'week', label: t('my_tutor_page.statistics.filter.week') });

    let data: any[] | null = null;

    const initData = async () => {
      try {
        const groupId = selectedGroup.id;

        data = await Promise.all([
          progressService.fetchOverviewClassProgress(user, groupId),
          EllReport.getGroupReport({
            groupId: groupId,
          }),
        ]);
      } catch (ex) {}

      if (!isActive) {
        return;
      }

      if (data) {
        const students: { [student_id: string]: ClassOverviewData } = {};
        ((data[0] as ClassProgressResponse | null)?.data?.data?.studentsInGroup || []).forEach(
          student => {
            students[student.id] = student;
          }
        );
        setStudents(students);

        setStudentsTopics(data[1] as GetGroupReportResponse);
      }

      setIsLoading(false);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [selectedGroup, user]);

  const filterFields: FilterField[] = useMemo(() => {
    if (!students) {
      return [];
    }

    const topics: string[] = [];
    Object.keys(studentsTopics).forEach(userId => {
      studentsTopics[userId].forEach(topic => {
        topics.push(topic.topic);
      });
    });
    [...new Set(topics)].map(topic => ({
      value: topic,
      label: topic,
    }));

    return [
      {
        key: 'level',
        label: t('my_tutor_page.group.filters.level'),
        options: [
          {
            value: 'All',
            label: t('my_tutor_page.group.filters.all'),
          },
          {
            value: '-1',
            label: t('inactive'),
          },
          {
            value: '0',
            label: t('levelTestIncomplete'),
          },
          ...[1, 2, 3, 4, 5].map(level => ({
            value: `${level}`,
            label: t(`levelName_${level}`),
          })),
        ],
      },
      {
        key: 'topic',
        label: t('my_tutor_page.group.filters.topic'),
        options: [
          {
            value: 'All',
            label: t('my_tutor_page.group.filters.all'),
          },
          ...[...new Set(topics)].map(topic => ({
            value: topic,
            label: topic,
          })),
        ],
      },
      {
        key: 'date',
        label: t('my_tutor_page.group.filters.date'),
        options: [
          {
            value: 'All',
            label: t('my_tutor_page.group.filters.all'),
          },
          {
            value: 'last7days',
            label: t('my_tutor_page.group.filters.last7days'),
          },
          {
            value: 'currentDate',
            label: t('my_tutor_page.group.filters.currentDate'),
            data: {
              type: 'date',
            },
          },
          {
            value: 'rangeDate',
            label: t('my_tutor_page.group.filters.rangeDate'),
            data: {
              type: 'date_range',
            },
          },
        ],
      },
    ].filter(f => f.options.length > 1);
  }, [t, studentsTopics]);

  const tableData = useMemo(() => {
    if (!students) {
      return [];
    }

    let studentsIds = Object.keys(students);

    if (filters.level && filters.level.value !== 'All') {
      studentsIds = studentsIds.filter(id => {
        const level =
          students[id].level ||
          (students[id].userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE ? 0 : -1);
        return level + '' === filters.level.value;
      });
    }

    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    if (filters.date) {
      if (filters.date.value === 'last7days') {
        const now = new Date();
        now.setHours(0);
        now.setMinutes(0);
        now.setSeconds(0);
        now.setMilliseconds(0);

        fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - 7);
        toDate = new Date(now);
        toDate.setDate(toDate.getDate() + 1);
      }

      if (filters.date.value === 'currentDate' && filters.date.data?.date) {
        fromDate = new Date(filters.date.data.date);
        fromDate.setHours(0);
        fromDate.setMinutes(0);
        fromDate.setSeconds(0);
        fromDate.setMilliseconds(0);
        toDate = new Date(fromDate);
        toDate.setDate(toDate.getDate() + 1);
      }

      if (
        filters.date.value === 'rangeDate' &&
        filters.date.data?.fromDate &&
        filters.date.data?.toDate
      ) {
        fromDate = new Date(filters.date.data.fromDate);
        fromDate.setHours(0);
        fromDate.setMinutes(0);
        fromDate.setSeconds(0);
        fromDate.setMilliseconds(0);

        toDate = new Date(filters.date.data.toDate);
        toDate.setHours(0);
        toDate.setMinutes(0);
        toDate.setSeconds(0);
        toDate.setMilliseconds(0);
        toDate.setDate(toDate.getDate() + 1);
      }
    }

    const tableData = studentsIds.map((userId: string) => {
      const userData = students[userId];
      let userTopics: GetGroupReportTopicsResponse[] = JSON.parse(
        JSON.stringify(studentsTopics[userId] || [])
      );

      let topics: number = 0;
      let lastTopic: string = '';
      let lastConversation: Date | null = null;
      let timeSpentWithTutor: number = 0;
      let numberOfConversations: number = 0;

      if (filters.topic && filters.topic.value !== 'All') {
        userTopics = userTopics.filter(t => t.topic === filters.topic.value);
      }

      if (fromDate || toDate) {
        const tempUserTopics: GetGroupReportTopicsResponse[] = [];
        userTopics.forEach(t => {
          while (
            toDate &&
            t.conversations.length > 0 &&
            new Date(t.conversations[0].createdDate) >= toDate
          ) {
            t.conversations.shift();
          }

          while (
            fromDate &&
            t.conversations.length > 0 &&
            new Date(t.conversations[t.conversations.length - 1].createdDate) < fromDate
          ) {
            t.conversations.pop();
          }

          if (t.conversations.length > 0) {
            t.lastCreatedDate = new Date(t.conversations[0].createdDate);
            tempUserTopics.push(t);
          }
        });
        userTopics = tempUserTopics;
        userTopics.sort((a, b) =>
          a.lastCreatedDate > b.lastCreatedDate ? -1 : a.lastCreatedDate < b.lastCreatedDate ? 1 : 0
        );
      }

      if (userTopics.length > 0) {
        topics = userTopics.length;
        lastTopic = userTopics[0].topic;
        lastConversation = new Date(userTopics[0].lastCreatedDate);

        timeSpentWithTutor = 0;
        numberOfConversations = 0;

        userTopics.forEach(topic => {
          timeSpentWithTutor =
            (timeSpentWithTutor || 0) +
            topic.conversations.reduce(
              (accumulator, currentValue) => accumulator + currentValue.chatTimeInSeconds,
              0
            );
          numberOfConversations = (numberOfConversations || 0) + topic.conversations.length;
        });
      }

      const avgConversationTime =
        timeSpentWithTutor && numberOfConversations
          ? timeSpentWithTutor / numberOfConversations
          : null;

      return {
        userId: userId,
        name: `${userData.firstName} ${userData.lastName}`,
        level:
          userData.level || (userData.userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE ? 0 : -1),
        topics: topics,
        lastTopic: lastTopic,
        lastConversation: lastConversation,
        conversation: numberOfConversations,
        avgConversationTime: avgConversationTime,
        reportData: studentsTopics[userId] || [],
      };
    });

    return tableData;
  }, [studentsTopics, students, filters]);

  const tableBlock = useMemo(() => {
    return (
      <div>
        <GenericTable
          rowKey={(row: any) => row.userId}
          startSortBy='name'
          noResultsMessage={t('my_tutor_page.group.table.noResultsMessage')}
          pageSize={tableData.length}
          columns={[
            {
              key: 'name',
              label: t('my_tutor_page.group.table.name'),
              sortable: true,
              render: value => {
                return <span className={classNames.studentsTableCellName}>{value}</span>;
              },
            },
            {
              key: 'level',
              label: t('my_tutor_page.group.table.level'),
              sortable: true,
              render: value => {
                if (value === -1)
                  return (
                    <span className={classNames.studentsTableCellLevelInactive}>
                      {t('inactive')}
                    </span>
                  );
                if (value === 0)
                  return (
                    <span className={classNames.studentsTableCellLevelTestIncomplete}>
                      {t('levelTestIncomplete')}
                    </span>
                  );
                return (
                  <span
                    className={classNames.studentsTableCellLevel}
                  >{`${value}-${t('levelName_' + value)}`}</span>
                );
              },
            },
            {
              key: 'topics',
              label: t('my_tutor_page.group.table.topics'),
              sortable: true,
              render: value => (
                <span className={classNames.studentsTableCellNumber}>{value || '-'}</span>
              ),
            },
            {
              key: 'lastTopic',
              label: t('my_tutor_page.group.table.lastTopic'),
              sortable: true,
              render: value => <span className={classNames.studentsTableCell}>{value || '-'}</span>,
            },
            {
              key: 'lastConversation',
              label: t('my_tutor_page.group.table.lastConversation'),
              sortable: true,
              render: value => {
                let text = '-';
                if (value) {
                  // text = `${value.getDate().toString().padStart(2, '0')}/${(value.getMonth() + 1).toString().padStart(2, '0')}/${value.getFullYear()} ${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`;
                  text = formatDateToStandard(value);
                }
                return <span className={classNames.studentsTableCell}>{text}</span>;
              },
            },
            {
              key: 'conversation',
              label: t('my_tutor_page.group.table.conversation'),
              sortable: true,
              render: value => (
                <span className={classNames.studentsTableCellNumber}>{value || '-'}</span>
              ),
            },
            {
              key: 'avgConversationTime',
              label: t('my_tutor_page.group.table.avgConversationTime'),
              sortable: true,
              render: value => {
                let text = '-';
                if (value) {
                  const fragments = getConversationDurationFragments(value);
                  text = `${fragments.hours ? fragments.hours + 'h ' : ''} ${fragments.minutes}m ${fragments.seconds}s`;
                }
                return <span className={classNames.studentsTableCell}>{text}</span>;
              },
            },
            {
              key: 'userId',
              label: '',
              width: '30px',
              render: (userId, row) => {
                return (
                  <Button
                    secondary
                    onClick={() => {
                      if (row.reportData) {
                        openReport(row.reportData, userId);
                      }
                    }}
                  >
                    <Icon icon='arrowRight' />
                  </Button>
                );
              },
            },
          ]}
          variant='compact'
          data={tableData}
        />
      </div>
    );
  }, [t, tableData, openReport]);

  const statisticsFilterFields = useMemo(() => {
    return [
      {
        value: 'week',
        label: t('my_tutor_page.statistics.filter.week'),
      },
      {
        value: 'month',
        label: t('my_tutor_page.statistics.filter.month'),
      },
      {
        value: 'rangeDate',
        label: t('my_tutor_page.statistics.filter.rangeDate'),
        data: {
          leaveOpen: true,
          type: 'date_range',
        },
      },
    ];
  }, [t, studentsTopics]);

  const statisticsData = useMemo(() => {
    const totalStudents = Object.keys(students || {}).length;

    if (totalStudents < 1) {
      return {
        totalStudents: 0,
        numberOfUsers: 0,
        numberOfConversations: 0,
        timeSpentWithTutor: 0,
        averageConversationTime: 0,
      };
    }

    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    if (statisticsFilters.value === 'week') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      fromDate = new Date(now);
      fromDate.setDate(fromDate.getDate() - 6);
      toDate = new Date(now);
      toDate.setDate(toDate.getDate() + 1);
    }

    if (statisticsFilters.value === 'month') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      fromDate = new Date(now);
      fromDate.setDate(fromDate.getDate() - 29);
      toDate = new Date(now);
      toDate.setDate(toDate.getDate() + 1);
    }

    if (
      statisticsFilters.value === 'rangeDate' &&
      statisticsFilters.data?.fromDate &&
      statisticsFilters.data?.toDate
    ) {
      fromDate = new Date(statisticsFilters.data.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      toDate = new Date(statisticsFilters.data.toDate);
      toDate.setHours(0, 0, 0, 0);
      toDate.setDate(toDate.getDate() + 1);
    }

    let numberOfUsers = 0;
    let numberOfConversations = 0;
    let timeSpentWithTutor = 0;

    const studentsTopicsD: GetGroupReportResponse = JSON.parse(JSON.stringify(studentsTopics));

    Object.keys(studentsTopicsD).forEach(studentId => {
      let userConversations = 0;
      let userTimeSpentWithTutor = 0;

      studentsTopicsD[studentId].forEach(t => {
        while (
          toDate &&
          t.conversations.length > 0 &&
          new Date(t.conversations[0].createdDate) >= toDate
        ) {
          t.conversations.shift();
        }

        while (
          fromDate &&
          t.conversations.length > 0 &&
          new Date(t.conversations[t.conversations.length - 1].createdDate) < fromDate
        ) {
          t.conversations.pop();
        }

        if (t.conversations.length > 0) {
          userConversations += t.conversations.length;
          userTimeSpentWithTutor += t.conversations.reduce(
            (accumulator, currentValue) => accumulator + currentValue.chatTimeInSeconds,
            0
          );
        }
      });

      if (userConversations) {
        numberOfUsers++;
        numberOfConversations += userConversations;
        timeSpentWithTutor += userTimeSpentWithTutor;
      }
    });

    const averageConversationTime =
      timeSpentWithTutor && numberOfConversations ? timeSpentWithTutor / numberOfConversations : 0;

    return {
      totalStudents: totalStudents,
      numberOfUsers: numberOfUsers,
      numberOfConversations: numberOfConversations,
      timeSpentWithTutor: timeSpentWithTutor,
      averageConversationTime: averageConversationTime,
    };
  }, [studentsTopics, students, statisticsFilters]);

  const statisticsBlock = useMemo(() => {
    const getPercents = (v1: number, v2: number) => {
      if (!v1) {
        return 0;
      }

      return (v2 * 100) / v1;
    };

    const filter = (
      <div className={classNames.filter}>
        <DropDown
          options={statisticsFilterFields}
          value={statisticsFilters}
          onChange={value => setStatisticsFilters(value)}
          staticTitle={t('my_tutor_page.statistics.filter.staticTitle') || ''}
          dropdownContainerClassName={classNames.filterDropDown}
          dropdownContainerOpenClassName={classNames.filterDropDownOpen}
        />
      </div>
    );

    const statisticBlocks: {
      blocks: StatisticsBlockProps[];
      filter?: any;
    }[] = [
      {
        blocks: [
          {
            staticValue: statisticsData.totalStudents,
            staticTitel: t('my_tutor_page.statistics.totalStudents'),
          },
        ],
      },
      {
        filter: filter,
        blocks: [
          {
            staticValue: statisticsData.numberOfUsers,
            staticValueDescription: `(${Number(getPercents(statisticsData.totalStudents, statisticsData.numberOfUsers).toFixed(2))}%)`,
            staticTitel: t('my_tutor_page.statistics.studentsUsingTutor'),
          },
          {
            staticValue: getConversationDurationStr(statisticsData.averageConversationTime),
            staticTitel: t('my_tutor_page.statistics.averageConversationTime'),
          },
        ],
      },
    ];

    return (
      <div className={classNames.statistics}>
        {statisticBlocks.map((statisticGroup, statisticGroupIndex) => {
          const itemsNumber = statisticGroup.blocks.length + (statisticGroup.filter ? 1 : 0);
          const gridTemplateColumns =
            statisticGroup.blocks.map(b => '1fr').join(' ') +
            (statisticGroup.filter ? ' 310px' : '');
          return (
            <div
              key={`statistic--${statisticGroupIndex}`}
              className={classNames.statisticGroup}
              style={{
                gridTemplateColumns: gridTemplateColumns,
                gridColumnEnd: `span ${itemsNumber}`,
              }}
            >
              {statisticGroup.blocks.map((statistic, statisticIndex) => {
                return (
                  <React.Fragment key={`statistic--${statisticGroupIndex}--${statisticIndex}`}>
                    <div className={classNames.statistic}>
                      <StatisticsBlock {...statistic} />
                    </div>
                  </React.Fragment>
                );
              })}
              {statisticGroup.filter}
            </div>
          );
        })}
      </div>
    );
  }, [t, statisticsFilterFields, statisticsData, statisticsFilters]);

  const headerBlock = useMemo(() => {
    return (
      <PageHeader
        title={t('my_tutor_page.group.table.title')}
        filterFields={filterFields}
        filters={filters}
        setFilters={setFilters}
      />
    );
  }, [t, filterFields, filters, setFilters]);

  const showView = useMemo(() => {
    if (isLoading) {
      return 'loading';
    }

    return 'group';
  }, [isLoading]);

  const loadingView = useMemo(() => {
    if (showView !== 'loading') {
      return;
    }

    return (
      <div className={classNames.myPrivateTutor}>
        <div className={classNames.fullPage}>
          {teachersClasses}
          <div className={classNames.fullPageContent}>
            <AppLoaderCircle />
          </div>
        </div>
      </div>
    );
  }, [showView, teachersClasses]);

  const groupView = useMemo(() => {
    if (showView !== 'group') {
      return;
    }

    return (
      <div className={classNames.myPrivateTutor}>
        {teachersClasses}
        {statisticsBlock}
        <div className={classNames.studentsTable}>
          {headerBlock}
          {tableBlock}
        </div>
      </div>
    );
  }, [showView, teachersClasses, statisticsBlock, headerBlock, tableBlock]);

  return (
    <>
      {loadingView}
      {groupView}
    </>
  );
};

export default GroupView;
